/**
 * Section offering-type semantics, shared across every app.
 *
 * `Section.offeringType` is a nullable free-text column, so every consumer that
 * asks "is this a community group?" has to normalize case before comparing. This
 * lived in the backend's sync module and was hand-mirrored in the web page
 * builder; both now import it from here so a widened set cannot silently make a
 * target the backend accepts unpickable in the admin UI.
 *
 * Plain data and a pure predicate — no Prisma, no Zod, no runtime dependency —
 * so backend, web, and mobile can all consume it.
 */

/**
 * Offering types whose section content is reliably detectable via the admin
 * token, and therefore stay on the SECTION_CONTENT fan-out lane (the types
 * `mapOfferingTypeToCategoryType` maps to the `'Community'` Blackbaud category).
 *
 * Every other offering type (ACADEMIC / ADVISORIES / ATHLETICS / ACTIVITIES /
 * DORMS) is served by the per-user USER_CONTENT_SYNC lane — the admin
 * section-content endpoint does not reliably return their content (SCH-225,
 * KTD0/KTD1).
 *
 * These are the same values the FEED page template accepts as a target.
 */
export const COMMUNITY_OFFERING_TYPES: ReadonlySet<string> = new Set(['COMMUNITY_GROUPS', 'GROUPS'])

/**
 * Returns true when an offering type is a community group. Upper-cases first —
 * the underlying column is free text and production data is not consistently
 * cased.
 */
export function isCommunityOfferingType(offeringType: string | null | undefined): boolean {
  return COMMUNITY_OFFERING_TYPES.has((offeringType ?? '').toUpperCase())
}
